import {
  FEATURE_CONFIG,
  FeatureDefinition,
  FeatureType,
  OrphanStatus,
} from '../config/featureConfig'
import { useAuthStore } from '../store/authStore'
import { getRuntimeEnv } from './runtimeEnv'

// ============================================================================
// FEATURE FLAG ENGINE
// ============================================================================
// Automatically manages feature flags based on configuration
// ============================================================================

export class FeatureFlagEngine {
  private enabledFeatures: Map<string, boolean> = new Map()
  private initialized = false

  constructor(list: string[], isSuperuser = false) {
    this.isSuperuser = isSuperuser
    this.initialize(list)
  }

  private isSuperuser: boolean

  private initialize(list: string[]): void {
    // Step 0: Validate configuration for H-cases
    this.validateConfiguration()

    // Step 1: Load raw values from environment
    this.loadFromEnvironment(list)

    // Step 2: Apply dependency rules
    this.applyDependencyRules()

    // Step 3: Auto-enable menu groups
    this.autoEnableMenuGroups()

    // Step 4: Apply parent visibility rules
    this.applyParentVisibilityRules()

    this.initialized = true
  }

  private validateConfiguration(): void {
    const errors: string[] = []
    const warnings: string[] = []
    const routes = new Map<string, string>()
    const featureIds = new Set<string>()

    FEATURE_CONFIG.forEach((feature) => {
      // H-Case: Duplicate feature IDs
      if (featureIds.has(feature.id)) {
        errors.push(`Duplicate feature ID: '${feature.id}'`)
      }
      featureIds.add(feature.id)

      // H-Case: Duplicate routes
      if (feature.route) {
        const existing = routes.get(feature.route)
        if (existing) {
          errors.push(`Duplicate route '${feature.route}' used by '${existing}' and '${feature.id}'`)
        }
        routes.set(feature.route, feature.id)
      }

      // H-Case: Self-referencing parent
      if (feature.parentId === feature.id) {
        errors.push(`Feature '${feature.id}' references itself as parent`)
      }

      // H-Case: Parent references non-existent feature
      if (feature.parentId) {
        const parentExists = FEATURE_CONFIG.some((f) => f.id === feature.parentId)
        if (!parentExists) {
          errors.push(`Feature '${feature.id}' references non-existent parent '${feature.parentId}'`)
        }
      }

      // H-Case: Children reference non-existent features
      if (feature.children) {
        feature.children.forEach((childId) => {
          const childExists = FEATURE_CONFIG.some((f) => f.id === childId)
          if (!childExists) {
            errors.push(`Feature '${feature.id}' references non-existent child '${childId}'`)
          }
        })
      }

      // H-Case: Route exists but component is null/undefined
      if (feature.route && !feature.component) {
        warnings.push(`Feature '${feature.id}' has route '${feature.route}' but no component`)
      }

      // H-Case: Menu group with route (should not have route)
      if (feature.type === FeatureType.MENU_GROUP && feature.route) {
        warnings.push(`Menu group '${feature.id}' should not have a route`)
      }

      // H-Case: Dependencies reference non-existent features
      if (feature.dependencies) {
        feature.dependencies.forEach((depId) => {
          const depExists = FEATURE_CONFIG.some((f) => f.id === depId)
          if (!depExists) {
            errors.push(`Feature '${feature.id}' has dependency on non-existent feature '${depId}'`)
          }
        })
      }
    })

    // H-Case: Detect circular dependencies
    const detectCircular = (featureId: string, visited: Set<string> = new Set()): boolean => {
      if (visited.has(featureId)) {
        return true // Circular dependency detected
      }

      const feature = FEATURE_CONFIG.find((f) => f.id === featureId)
      if (!feature || !feature.parentId) {
        return false
      }

      visited.add(featureId)
      return detectCircular(feature.parentId, visited)
    }

    FEATURE_CONFIG.forEach((feature) => {
      if (feature.parentId && detectCircular(feature.id)) {
        errors.push(`Circular dependency detected involving feature '${feature.id}'`)
      }
    })

    // Log errors and warnings
    if (errors.length > 0) {

      errors.forEach((err) => { })
      throw new Error(`Feature flag configuration has ${errors.length} error(s). Check console for details.`)
    }

    if (warnings.length > 0) {

      warnings.forEach((warn) => { })
    }

    if (errors.length === 0 && warnings.length === 0) {

    }
  }

  private loadFromEnvironment(permissionsList: string[] = []): void {
    FEATURE_CONFIG.forEach((feature) => {
      const envValue = getRuntimeEnv(`REACT_APP_${feature.envKey}`)
      const enabledFeature = envValue !== undefined ? envValue === 'true' : feature.defaultEnabled
      const hasPermission = feature.permissionKey ? permissionsList.some((p) => p === feature.permissionKey) : true
      // Superuser-only features are an identity fact: hidden for everyone else.
      const allowedIdentity = feature.superuserOnly ? this.isSuperuser : true
      this.enabledFeatures.set(feature.id, (enabledFeature && hasPermission && allowedIdentity))
    })
  }

  private applyDependencyRules(): void {
    FEATURE_CONFIG.forEach((feature) => {
      // Rule: Orphaned children auto-disable when parent disabled
      if (feature.parentId && feature.orphanStatus === OrphanStatus.ORPHANED) {
        const parentEnabled = this.enabledFeatures.get(feature.parentId)
        const childEnabled = this.enabledFeatures.get(feature.id)

        if (!parentEnabled && childEnabled) {
          this.enabledFeatures.set(feature.id, false)
        }
      }

      // Rule: Features with dependencies auto-disable when any dependency is disabled
      if (feature.dependencies && feature.dependencies.length > 0) {
        const featureEnabled = this.enabledFeatures.get(feature.id)

        if (featureEnabled) {
          const missingDeps = feature.dependencies.filter(
            (depId) => !this.enabledFeatures.get(depId)
          )

          if (missingDeps.length > 0) {
            this.enabledFeatures.set(feature.id, false)
          }
        }
      }
    })
  }

  private autoEnableMenuGroups(): void {
    FEATURE_CONFIG.filter((f) => f.type === FeatureType.MENU_GROUP).forEach((menuGroup) => {
      // Menu group enabled if ANY child is enabled
      const children = FEATURE_CONFIG.filter((f) => f.parentId === menuGroup.id)
      const anyChildEnabled = children.some((child) => this.enabledFeatures.get(child.id))

      this.enabledFeatures.set(menuGroup.id, anyChildEnabled)
    })
  }

  private applyParentVisibilityRules(): void {
    FEATURE_CONFIG.filter((f) => f.type === FeatureType.PARENT).forEach((parent) => {
      // Parent visible if at least ONE child enabled
      const children = FEATURE_CONFIG.filter((f) => f.parentId === parent.id)
      const anyChildEnabled = children.some((child) => this.enabledFeatures.get(child.id))

      if (!anyChildEnabled) {
        this.enabledFeatures.set(parent.id, false)
      }
    })
  }

  // ========================================================================
  // PUBLIC API
  // ========================================================================

  public isFeatureEnabled(featureId: string): boolean {
    if (!this.initialized) {

      return false
    }
    return this.enabledFeatures.get(featureId) ?? false
  }
  public isGroupFeatureEnabled(featuresId: string[]): boolean {
    if (!this.initialized) {

      return false
    }
    return featuresId.some((featureId) => this.enabledFeatures.get(featureId) ?? false)
  }

  public isRouteEnabled(routePath: string): boolean {
    const feature = FEATURE_CONFIG.find((f) => f.route === routePath)
    return feature ? this.isFeatureEnabled(feature.id) : false
  }

  public getEnabledFeatures(): FeatureDefinition[] {
    return FEATURE_CONFIG.filter((f) => this.isFeatureEnabled(f.id))
  }

  public getSidebarItems(): FeatureDefinition[] {
    return this.getEnabledFeatures()
      .filter((f) => f.sidebar?.show && !f.parentId) // Exclude children with parents - they appear in submenus
      .sort((a, b) => (a.sidebar?.order ?? 0) - (b.sidebar?.order ?? 0))
  }

  public getRoutes(): FeatureDefinition[] {
    return this.getEnabledFeatures().filter((f) => f.route && f.component)
  }
  public getRouteConfig(routePath: string): FeatureDefinition | undefined {
    return this.getEnabledFeatures().find((f) => f.route === routePath)
  }

  public getChildFeatures(parentId: string): FeatureDefinition[] {
    return this.getEnabledFeatures().filter((f) => f.parentId === parentId)
  }

  public getAllFeatures(): FeatureDefinition[] {
    return FEATURE_CONFIG
  }

  public getFeatureConfig(featureId: string): FeatureDefinition | undefined {
    return FEATURE_CONFIG.find((f) => f.id === featureId)
  }
}

// Singleton instance

export const useFeature = () => {
  const permissionsList = useAuthStore((state) => state.permissionList)
  const isSuperuser = useAuthStore((state) => state.userProfile?.is_admin === true)

  const featureFlagEngine = new FeatureFlagEngine(permissionsList, isSuperuser)


  const sidebarPermission = () => {
    return featureFlagEngine.getSidebarItems()
  }
  const childrenPermission = (id: string) => {
    return featureFlagEngine.getChildFeatures(id)
  }
  const hasPermission = (id: string) => {
    return featureFlagEngine.isFeatureEnabled(id)
  }
  const hasPermissionGroup = (list: string[]) => {
    return featureFlagEngine.isGroupFeatureEnabled(list)
  }
  const getRoutePermission = () => {
    return featureFlagEngine.getRoutes()
  }

  return {
    isEnabled: (featureId: string) => featureFlagEngine.isFeatureEnabled(featureId),
    isRouteEnabled: (routePath: string) => featureFlagEngine.isRouteEnabled(routePath),
    getEnabledFeatures: () => featureFlagEngine.getEnabledFeatures(),
    getSidebarItems: () => featureFlagEngine.getSidebarItems(),
    getRoutes: () => featureFlagEngine.getRoutes(),
    getMainRoute: () => featureFlagEngine.getRoutes()?.find((i) => i?.mainRoute)?.route,
    getChildFeatures: (parentId: string) => featureFlagEngine.getChildFeatures(parentId),
    featureFlagEngine,
    getRoutePermission,
    hasPermissionGroup,
    hasPermission,
    sidebarPermission,
    childrenPermission
  }

}
